import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import authService from "../services/authService";
import authStorage from "../storage/authStorage";
import { hasSupreqAccess } from "../utils/tokenPermissions";

interface ILogin {
  id: string;
  secret: string;
}

interface IAuthContext {
  token: string;
  loading: boolean;
  hasAccess: boolean;
  setToken: (data: string) => void;
  login: (data: ILogin) => void;
  logout: () => void;
}

export const AuthContext = createContext<IAuthContext>({
  token: "",
  setToken: () => {},
  loading: true,
  hasAccess: false,
  logout: () => {},
  login: () => {},
});
const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const login = async ({ id, secret }: ILogin) => {
    try {
      const res = await authService.login({ id, secret });

      if (!res.access_token) {
        setToken("");
        throw new Error("INVALID_CREDENTIALS");
      }

      if (!hasSupreqAccess(res.access_token)) {
        setToken("");
        authStorage.remove();
        throw new Error("FORBIDDEN");
      }

      setToken(res.access_token);
    } catch (error: unknown) {
      setToken("");
      console.log(error);
      throw error;
    }
  };
  const logout = useCallback(async () => {
    setToken("");
    authStorage.remove();
  }, [setToken]);
  useEffect(() => {
    const storedToken = authStorage.get();

    if (storedToken && hasSupreqAccess(storedToken)) {
      setToken(storedToken);
    } else if (storedToken) {
      authStorage.remove();
      setToken("");
    }

    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);
  useEffect(() => {
    authStorage.set(token);
  }, [token]);

  // Listener para evento de logout automático (disparado quando credenciais expiram)
  useEffect(() => {
    const handleLogoutRequired = () => {
      logout();
    };

    window.addEventListener("auth:logout-required", handleLogoutRequired);

    return () => {
      window.removeEventListener("auth:logout-required", handleLogoutRequired);
    };
  }, [logout]);

  const hasAccess = Boolean(token) && hasSupreqAccess(token);

  return (
    <AuthContext.Provider
      value={{
        token,
        loading,
        hasAccess,
        setToken,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = (): IAuthContext => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export { AuthProvider, useAuth };
